
<template>
  <v-app>
    <v-main class="main">
      <AdminSidebar
        :drawerOpen="drawerOpen"
        :collapsed="isSidebarCollapsed"
        :isMobile="isMobile"
        @update:drawerOpen="drawerOpen = $event"
      />

      <v-container fluid class="main-content pa-4">
        <Header
          :isDarkMode="isDarkMode"
          :isMobile="isMobile"
          @toggle-sidebar="toggleSidebar"
        />

        <v-card class="table-card mt-6">
          <div
            class="table-header d-flex justify-space-between align-center flex-wrap ga-3"
          >
            <div class="d-flex align-center flex-wrap ga-3">
              <InputText
                v-model="filters.global.value"
                placeholder="Search fee configurations..."
              />

              <Button
                type="button"
                icon="pi pi-filter-slash"
                label="Clear"
                variant="outlined"
                @click="clearFilters"
              />
            </div>

            <button
              type="button"
              :disabled="loading"
              class="theme-button px-5"
              @click="openCreateModal"
            >
              <v-icon start>mdi-plus</v-icon>
              Save Fee Configuration
            </button>
          </div>

          <DataTable
            v-model:filters="filters"
            :value="feesConfig"
            paginator
            :rows="10"
            dataKey="uuid"
            stripedRows
            class="modern-table"
            :rowsPerPageOptions="[5, 10, 20, 50]"
            tableStyle="min-width: 55rem"
            filterDisplay="menu"
            :globalFilterFields="[
              'key',
              'description',
              'type',
              'calculation_type'
            ]"
          >
            <template #empty>
              No fee configurations found.
            </template>

            <template #loading>
              Loading fee configurations...
            </template>

            <Column header="Name">
              <template #body="{ data }">
                {{ data.key }}
              </template>
            </Column>

            <Column header="Category">
              <template #body="{ data }">
                {{ formatLabel(data.type) }}
              </template>
            </Column>

            <Column header="Calculation Type">
              <template #body="{ data }">
                {{ formatLabel(data.calculation_type) }}
              </template>
            </Column>

            <Column header="Default Value">
              <template #body="{ data }">
                {{ formatFeeValue(data.value, data.calculation_type) }}
              </template>
            </Column>

            <Column header="Fee Tiers">
              <template #body="{ data }">
                <div class="tier-summary">
                  <span class="font-weight-bold">
                    {{ data.tiers?.length ?? 0 }}
                    {{ (data.tiers?.length ?? 0) === 1 ? 'tier' : 'tiers' }}
                  </span>

                  <span
                    v-if="data.tiers?.length"
                    class="text-medium-emphasis text-caption"
                  >
                    {{ formatTierSummary(data.tiers) }}
                  </span>
                </div>
              </template>
            </Column>

            <Column header="Description">
              <template #body="{ data }">
                {{ data.description || '—' }}
              </template>
            </Column>

            <Column header="Status">
              <template #body="{ data }">
                <span
                  :class="[
                    'status-pill',
                    data.status ? 'status-active' : 'status-inactive'
                  ]"
                >
                  {{ data.status ? 'Active' : 'Inactive' }}
                </span>
              </template>
            </Column>

            <Column header="Added On">
              <template #body="{ data }">
                {{ formatDateTime(data.created_at) }}
              </template>
            </Column>

            <Column header="" style="width: 120px">
              <template #body="{ data }">
                <v-menu>
                  <template #activator="{ props }">
                    <v-btn icon v-bind="props" variant="text">
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>

                  <v-list>
                    <v-list-item @click="openEditModal(data)">
                      Edit {{ data.key }} Details
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </Column>
          </DataTable>
        </v-card>

        <!-- Create / Update Fee Configuration -->
        <v-dialog
          v-model="feeConfigDialog"
          max-width="850"
          persistent
          scrollable
        >
          <v-card class="pa-5 pa-md-6">
            <v-card-title class="text-h6 mb-4 text-center">
              {{
                isEditing
                  ? 'Edit Fee Configuration'
                  : 'Create Fee Configuration'
              }}
            </v-card-title>

            <v-card-text class="pa-0">
              <form @submit.prevent="handleCreateFee">
                <!-- Configuration key -->
                <div class="form-field">
                  <label class="form-label">
                    Name / Configuration Key
                  </label>

                  <v-combobox
                    v-model="form.key"
                    :items="keyItems"
                    item-title="label"
                    item-value="value"
                    variant="outlined"
                    :rules="[v => !!getKeyValue(v) || 'Key is required']"
                    clearable
                    :disabled="isEditing"
                    placeholder="Select or enter a configuration key"
                  />
                </div>

                <!-- Description -->
                <div class="form-field">
                  <label class="form-label">Description</label>

                  <v-textarea
                    v-model="form.description"
                    variant="outlined"
                    rows="2"
                    auto-grow
                    placeholder="Describe this fee configuration"
                  />
                </div>

                <!-- Category -->
                <div class="form-field">
                  <label class="form-label">Category</label>

                  <v-select
                    v-model="form.type"
                    :items="feeTypes"
                    item-title="label"
                    item-value="value"
                    variant="outlined"
                    :rules="[v => !!v || 'Category is required']"
                    placeholder="Select category"
                  />
                </div>

                <!-- Calculation type belongs to the parent configuration -->
                <div class="form-field">
                  <label class="form-label">Calculation Type</label>

                  <v-select
                    v-model="form.calculation_type"
                    :items="calculationTypes"
                    item-title="label"
                    item-value="value"
                    variant="outlined"
                    :rules="[
                      v => !!v || 'Calculation type is required'
                    ]"
                    placeholder="Select calculation type"
                  />

                  <p class="text-caption text-medium-emphasis">
                    This calculation type applies to every tier in this
                    configuration.
                  </p>
                </div>

                <!-- Parent value -->
                <div class="form-field">
                  <label class="form-label">
                    Default Fee Value
                    ({{ calculationUnit }})
                  </label>

                  <v-text-field
                    v-model="form.value"
                    type="number"
                    min="0"
                    step="0.01"
                    variant="outlined"
                    placeholder="Enter default fee value"
                    :error-messages="getDefaultValueErrors"
                    hide-details="auto"
                  />

                  <p class="text-caption text-medium-emphasis">
                    This is the configuration-level value. Each tier has its
                    own value used for tier-based calculations.
                  </p>
                </div>

                <v-divider class="my-4" />

                <!-- Fee tiers -->
                <div class="tiers-header">
                  <div>
                    <h3 class="text-subtitle-1 font-weight-bold">
                      Fee Tiers
                    </h3>

                    <p class="text-caption text-medium-emphasis mt-1">
                      Configure the amount range and the
                      {{ form.calculation_type === 'percentage'
                        ? 'percentage'
                        : 'fixed amount'
                      }}
                      charged within each range.
                    </p>
                  </div>
                </div>

                <v-alert
                  type="info"
                  variant="tonal"
                  density="compact"
                  class="mb-4"
                >
                  Minimum amounts are inclusive and maximum amounts are
                  exclusive. Leave the maximum empty for a tier with no
                  upper limit. All tiers use the calculation type selected
                  above.
                </v-alert>

                <div
                  v-for="(tier, index) in form.tiers"
                  :key="tier.id"
                  class="tier-card mb-4"
                >
                  <div class="tier-card-header">
                    <div class="d-flex align-center ga-2">
                      <span class="tier-number">{{ index + 1 }}</span>

                      <span class="font-weight-bold">
                        Tier {{ index + 1 }}
                      </span>
                    </div>

                    <v-btn
                      v-if="form.tiers.length > 1"
                      icon="mdi-delete-outline"
                      variant="text"
                      color="error"
                      size="small"
                      type="button"
                      aria-label="Remove tier"
                      @click="removeTier(index)"
                    />
                  </div>

                  <!-- Minimum and maximum amounts -->
                  <v-row dense class="mt-2">
                    <v-col cols="12" sm="6">
                      <label class="form-label">
                        Minimum Amount (₦)
                      </label>

                      <v-text-field
                        v-model="tier.min_amount"
                        type="number"
                        min="0"
                        step="0.01"
                        variant="outlined"
                        density="comfortable"
                        placeholder="0"
                        :error-messages="
                          getTierErrors(index, 'min_amount')
                        "
                        hide-details="auto"
                      />
                    </v-col>

                    <v-col cols="12" sm="6">
                      <label class="form-label">
                        Maximum Amount (₦)
                      </label>

                      <v-text-field
                        v-model="tier.max_amount"
                        type="number"
                        min="0"
                        step="0.01"
                        variant="outlined"
                        density="comfortable"
                        placeholder="No upper limit"
                        :error-messages="
                          getTierErrors(index, 'max_amount')
                        "
                        hide-details="auto"
                      />
                    </v-col>
                  </v-row>

                  <!-- Tier value inherits the parent calculation type -->
                  <div class="form-field mt-3 mb-0">
                    <label class="form-label">
                      Tier Fee Value ({{ calculationUnit }})
                    </label>

                    <v-text-field
                      v-model="tier.value"
                      type="number"
                      min="0"
                      step="0.01"
                      variant="outlined"
                      density="comfortable"
                      :placeholder="
                        form.calculation_type === 'percentage'
                          ? 'e.g. 1.5'
                          : 'e.g. 200'
                      "
                      :error-messages="getTierErrors(index, 'value')"
                      hide-details="auto"
                    />
                  </div>
                </div>

                <!-- Add tier -->
                <button
                  type="button"
                  class="add-tier-button"
                  @click="addTier"
                >
                  <v-icon size="18">mdi-plus-circle-outline</v-icon>
                  Add Another Tier
                </button>

                <p class="text-caption text-medium-emphasis mt-2">
                  Example: ₦0–₦50,000, ₦50,000–₦100,000, and
                  ₦100,000–₦300,000. Ensure the ranges do not overlap.
                </p>

                <!-- Overall status -->
                <v-divider class="my-4" />

                <div class="overall-status">
                  <div>
                    <div class="text-body-2 font-weight-medium">
                      Configuration Status
                    </div>

                    <div class="text-caption text-medium-emphasis">
                      Enable or disable this fee configuration.
                    </div>
                  </div>

                  <v-switch
                    v-model="form.status"
                    color="green"
                    :label="form.status ? 'Active' : 'Inactive'"
                    hide-details
                  />
                </div>

                <!-- Actions -->
                <div class="form-actions">
                  <v-btn
                    variant="text"
                    type="button"
                    :disabled="feeLoading"
                    @click="feeConfigDialog = false"
                  >
                    Cancel
                  </v-btn>

                  <v-btn
                    color="black"
                    type="submit"
                    :loading="feeLoading"
                    :disabled="!isFormValid"
                  >
                    {{
                      isEditing
                        ? 'Update Configuration'
                        : 'Save Configuration'
                    }}
                  </v-btn>
                </div>
              </form>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Notifications -->
        <v-snackbar
          v-model="snackbar.show"
          :color="snackbar.color"
          timeout="3000"
          location="top right"
        >
          {{ snackbar.message }}

          <template #actions>
            <v-btn
              variant="outlined"
              @click="snackbar.show = false"
            >
              Close
            </v-btn>
          </template>
        </v-snackbar>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue"
import AdminSidebar from "@/components/AdminSidebar.vue"
import Header from "@/components/Header.vue"
import { useInvStore } from "@/store/investment"
import { formatDateTime, getAllErrors } from "@/util"
import {
  executeFeeConfig,
  executeUpdateConfig
} from "@/api/investment"

const fee = useInvStore()

const isSidebarCollapsed = ref(false)
const drawerOpen = ref(false)
const isMobile = ref(false)
const isDarkMode = ref(false)

const filters = ref({
  global: {
    value: null,
    matchMode: "contains"
  }
})

const feeConfigDialog = ref(false)
const feeLoading = ref(false)
const loading = ref(false)

const isEditing = ref(false)
const editingId = ref(null)

const feesConfig = computed(() => fee.fees || [])

const snackbar = ref({
  show: false,
  message: "",
  color: "success"
})

let nextTierId = 1

const makeTier = (overrides = {}) => ({
  id: nextTierId++,
  min_amount: 0,
  max_amount: null,
  value: "",
  ...overrides
})

const makeDefaultForm = () => ({
  key: "",
  value: "",
  description: "",
  type: "",
  calculation_type: "fixed",
  status: true,
  tiers: [makeTier()]
})

const form = ref(makeDefaultForm())

const keyItems = [
  { label: "Deposit", value: "deposit" },
  { label: "Repayment", value: "repayment" },
  { label: "Withdrawal", value: "withdrawal" },
  { label: "Investment", value: "investment" }
]

const calculationTypes = [
  { label: "Fixed Amount (₦)", value: "fixed" },
  { label: "Percentage (%)", value: "percentage" }
]

const feeTypes = [
  { label: "Charge", value: "charge" },
  { label: "Tax", value: "tax" }
]

const calculationUnit = computed(() => {
  return form.value.calculation_type === "percentage" ? "%" : "₦"
})

/* Responsive layout */
const toggleSidebar = () => {
  if (isMobile.value) {
    drawerOpen.value = !drawerOpen.value
  } else {
    isSidebarCollapsed.value = !isSidebarCollapsed.value
  }
}

const checkMobile = () => {
  isMobile.value = window.innerWidth < 768
  drawerOpen.value = !isMobile.value
}

/* Lifecycle */
onMounted(async () => {
  checkMobile()
  window.addEventListener("resize", checkMobile)

  loading.value = true

  try {
    await fee.loadFees()
  } catch (error) {
    console.error(error)
    showSnackbar("Failed to load fee configurations.", "error")
  } finally {
    loading.value = false
  }
})

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile)
})

/* Snackbar */
const showSnackbar = (message, color = "success") => {
  snackbar.value = {
    show: true,
    message,
    color
  }
}

/* Search */
const clearFilters = () => {
  filters.value = {
    global: {
      value: null,
      matchMode: "contains"
    }
  }
}

/* Normalization helpers */
const getKeyValue = (value) => {
  if (value && typeof value === "object") {
    return value.value ?? value.title ?? value.label ?? ""
  }

  return String(value ?? "").trim()
}

const normalizeCalculationType = (value) => {
  const normalized = String(value ?? "fixed").toLowerCase()

  return normalized === "percentage" ? "percentage" : "fixed"
}

const normalizeFeeCategory = (value) => {
  const normalized = String(value ?? "").toLowerCase()

  if (normalized === "tax") return "tax"
  if (normalized === "fee") return "fee"

  return "charge"
}

const formatLabel = (value) => {
  if (!value) return "—"

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, character => character.toUpperCase())
}

const formatFeeValue = (value, calculationType) => {
  if (value === null || value === undefined || value === "") {
    return "—"
  }

  const amount = Number(value)

  if (!Number.isFinite(amount)) return "—"

  const formatted = amount.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })

  return normalizeCalculationType(calculationType) === "percentage"
    ? `${formatted}%`
    : `₦${formatted}`
}

/* Reset and create */
const resetForm = () => {
  nextTierId = 1
  form.value = makeDefaultForm()
}

const openCreateModal = () => {
  resetForm()
  isEditing.value = false
  editingId.value = null
  feeConfigDialog.value = true
}

/* Tier management */
const addTier = () => {
  const tiers = form.value.tiers
  const previousTier = tiers[tiers.length - 1]

  const nextMinimum =
    previousTier?.max_amount !== null &&
    previousTier?.max_amount !== "" &&
    previousTier?.max_amount !== undefined
      ? Number(previousTier.max_amount)
      : 0

  tiers.push(
    makeTier({
      min_amount: nextMinimum,
      max_amount: null,
      value: ""
    })
  )
}

const removeTier = (index) => {
  if (form.value.tiers.length <= 1) {
    showSnackbar(
      "At least one fee tier is required.",
      "warning"
    )
    return
  }

  form.value.tiers.splice(index, 1)
}

/* Edit existing configuration */
const openEditModal = (item) => {
  isEditing.value = true
  editingId.value = item.uuid
  nextTierId = 1

  const calculationType = normalizeCalculationType(
    item.calculation_type
  )

  const existingTiers =
    Array.isArray(item.tiers) && item.tiers.length
      ? item.tiers
      : [
          {
            min_amount: 0,
            max_amount: null,
            value: item.value ?? ""
          }
        ]

  form.value = {
    key: item.key || "",
    value: item.value ?? "",
    description: item.description || "",
    type: normalizeFeeCategory(item.type),
    calculation_type: calculationType,
    status:
      item.status === true ||
      item.status === 1 ||
      item.status === "1",
    tiers: existingTiers.map(tier =>
      makeTier({
        min_amount: tier.min_amount ?? 0,
        max_amount: tier.max_amount ?? null,
        value: tier.value ?? ""
      })
    )
  }

  feeConfigDialog.value = true
}

/* Field validation */
const getDefaultValueErrors = computed(() => {
  const value = form.value.value

  if (
    value === "" ||
    value === null ||
    value === undefined ||
    !Number.isFinite(Number(value))
  ) {
    return ["Default fee value is required."]
  }

  if (Number(value) < 0) {
    return ["Default fee value cannot be negative."]
  }

  return []
})

const getTierErrors = (index, field) => {
  const tier = form.value.tiers[index]

  if (!tier) return []

  const min =
    tier.min_amount === "" ||
    tier.min_amount === null ||
    tier.min_amount === undefined
      ? NaN
      : Number(tier.min_amount)

  const max =
    tier.max_amount === "" ||
    tier.max_amount === null ||
    tier.max_amount === undefined
      ? null
      : Number(tier.max_amount)

  if (field === "min_amount") {
    if (!Number.isFinite(min)) {
      return ["Minimum amount is required."]
    }

    if (min < 0) {
      return ["Minimum amount cannot be negative."]
    }
  }

  if (field === "max_amount" && max !== null) {
    if (!Number.isFinite(max) || !Number.isFinite(min) || max <= min) {
      return ["Maximum must be greater than minimum."]
    }
  }

  if (field === "value") {
    if (
      tier.value === "" ||
      tier.value === null ||
      tier.value === undefined ||
      !Number.isFinite(Number(tier.value))
    ) {
      return ["Tier fee value is required."]
    }

    if (Number(tier.value) < 0) {
      return ["Tier fee value cannot be negative."]
    }
  }

  return []
}

/* Tier range validation */
const hasValidTierRanges = computed(() => {
  const tiers = form.value.tiers

  if (!tiers.length) return false

  const normalized = tiers.map(tier => ({
    min:
      tier.min_amount === "" ||
      tier.min_amount === null ||
      tier.min_amount === undefined
        ? NaN
        : Number(tier.min_amount),
    max:
      tier.max_amount === "" ||
      tier.max_amount === null ||
      tier.max_amount === undefined
        ? null
        : Number(tier.max_amount)
  }))

  for (const tier of normalized) {
    if (!Number.isFinite(tier.min) || tier.min < 0) {
      return false
    }

    if (
      tier.max !== null &&
      (!Number.isFinite(tier.max) || tier.max <= tier.min)
    ) {
      return false
    }
  }

  const sorted = [...normalized].sort((a, b) => a.min - b.min)

  for (let index = 1; index < sorted.length; index++) {
    const previous = sorted[index - 1]
    const current = sorted[index]

    // A tier without an upper limit must be the last tier.
    if (previous.max === null) return false

    // Adjacent ranges are allowed; overlapping ranges are not.
    if (current.min < previous.max) return false
  }

  return true
})

const isFormValid = computed(() => {
  const key = getKeyValue(form.value.key)

  if (!key) return false
  if (!form.value.type) return false
  if (!form.value.calculation_type) return false

  if (getDefaultValueErrors.value.length) return false

  if (!form.value.tiers.length || !hasValidTierRanges.value) {
    return false
  }

  return form.value.tiers.every(tier => {
    return (
      tier.value !== "" &&
      tier.value !== null &&
      tier.value !== undefined &&
      Number.isFinite(Number(tier.value)) &&
      Number(tier.value) >= 0
    )
  })
})

/* Table summary */
const formatTierSummary = (tiers) => {
  if (!Array.isArray(tiers) || !tiers.length) return ""

  const sorted = [...tiers].sort(
    (a, b) => Number(a.min_amount) - Number(b.min_amount)
  )

  const first = sorted[0]
  const last = sorted[sorted.length - 1]

  const formatAmount = value =>
    Number(value).toLocaleString(undefined, {
      maximumFractionDigits: 2
    })

  if (sorted.length === 1) {
    if (first.max_amount === null || first.max_amount === "") {
      return `From ₦${formatAmount(first.min_amount)}`
    }

    return `₦${formatAmount(first.min_amount)} – ₦${formatAmount(first.max_amount)}`
  }

  if (last.max_amount === null || last.max_amount === "") {
    return `From ₦${formatAmount(first.min_amount)}`
  }

  return (
    `₦${formatAmount(first.min_amount)} – ` +
    `₦${formatAmount(last.max_amount)}`
  )
}

/* Build API payload */
const buildPayload = () => {
  const key = getKeyValue(form.value.key)

  return {
    key,
    value: Number(form.value.value),
    description: form.value.description?.trim() || null,
    type: form.value.type,
    calculation_type: form.value.calculation_type,
    status: Boolean(form.value.status),

    tiers: form.value.tiers.map(tier => ({
      min_amount: Number(tier.min_amount),
      max_amount:
        tier.max_amount === "" ||
        tier.max_amount === null ||
        tier.max_amount === undefined
          ? null
          : Number(tier.max_amount),
      value: Number(tier.value)
    }))
  }
}

/* Submit create or update */
const handleCreateFee = async () => {
  if (!isFormValid.value) {
    showSnackbar(
      "Please complete the configuration and check the tier ranges.",
      "error"
    )
    return
  }

  feeLoading.value = true

  try {
    const payload = buildPayload()

    if (isEditing.value) {
      await executeUpdateConfig(editingId.value, payload)
    } else {
      await executeFeeConfig(payload)
    }

    await fee.loadFees()

    showSnackbar(
      isEditing.value
        ? "Fee configuration updated successfully."
        : "Fee configuration created successfully."
    )

    feeConfigDialog.value = false
    resetForm()
    isEditing.value = false
    editingId.value = null
  } catch (error) {
    console.error(error)

    const message = getAllErrors(error)

    showSnackbar(
      message || "Failed to save fee configuration.",
      "error"
    )
  } finally {
    feeLoading.value = false
  }
}
</script>

<style scoped>
.main {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  background: #f8fafc;
}

.table-card {
  padding: 20px;
  border-radius: 14px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 20px;
}

.modern-table {
  border: none;
}

.theme-button {
  min-height: 46px;
  background: #000;
  color: #fff;
  border-radius: 5px;
  font-weight: 600;
  transition: background 0.25s ease;
}

.theme-button:hover {
  background: #292828;
}

.theme-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 5px;
}

.tiers-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.tier-card {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  background: #fff;
}

.tier-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tier-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  background: #eef2ff;
  color: #3730a3;
  font-size: 12px;
  font-weight: 700;
}

.add-tier-button {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 0;
  color: #166534;
  font-size: 13px;
  font-weight: 600;
  background: transparent;
  border: none;
  cursor: pointer;
}

.add-tier-button:hover {
  color: #14532d;
  text-decoration: underline;
}

.overall-status {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding-top: 22px;
}

.tier-summary {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.status-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-active {
  background: #ecfdf5;
  color: #16a34a;
}

.status-inactive {
  background: #fef2f2;
  color: #dc2626;
}

:deep(.modern-table .p-datatable-thead > tr > th) {
  background: #eef7f0;
  color: #065f46;
  font-weight: 600;
  border-bottom: 2px solid #c1edc3;
}

:deep(.p-datatable-tbody > tr:hover) {
  background-color: #f5f5f5;
}

@media (max-width: 600px) {
  .table-card {
    padding: 12px;
  }

  .tier-card {
    padding: 12px;
  }

  .form-actions {
    flex-wrap: wrap;
  }
}
</style>
